// Inventory Phase 3 — Sales integration admin (exceptions + order lookup).
//
// Everything that needs a person after sales integration is on:
//   * SHORTFALL — a PAID order whose stock could not be deducted (nothing
//     was deducted; stock never goes negative). Retry once stock arrives,
//     or resolve it (e.g. the order was refunded) with a note.
//   * Restock pending — a CANCELLED order whose stock was already deducted.
//     A cancellation or refund never restocks automatically; staff restock
//     explicitly, which posts an audited SALE_REVERSAL to the exact lots.
// Quantities are server strings; this page does no arithmetic.

import React, { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { toast } from "sonner";
import { Field, fail } from "./fields";
import { newOperationKey } from "../../lib/inventory";

const STATE_VARIANT = { RESERVED: "default", CONSUMED: "secondary", SHORTFALL: "destructive" };

function AllocationLines({ allocation }) {
  return (
    <div className="text-xs text-muted-foreground">
      {(allocation.lines || []).map((l) => (
        <div key={l.line_id}>
          {l.product_name || l.product_id} → <span className="font-mono">{l.item_code}</span> · {l.base_per_unit} {l.base_uom} per unit
          {allocation.consumed_by_line?.[l.line_id] && <> · deducted {allocation.consumed_by_line[l.line_id]} {l.base_uom}</>}
          {(allocation.restocked_line_ids || []).includes(l.line_id) && <Badge variant="outline" className="ml-1">restocked</Badge>}
        </div>
      ))}
    </div>
  );
}

export function SalesPanel({ settings, onChanged }) {
  const [ex, setEx] = useState({ shortfalls: [], restock_pending: [] });
  const [lookup, setLookup] = useState("");
  const [found, setFound] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setEx((await api.get("/inventory/sales/exceptions")).data); } catch (e) { fail(e, "Could not load sales exceptions"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, ok) => {
    setBusy(true);
    try { const res = await fn(); toast.success(typeof ok === "function" ? ok(res) : ok); await load(); onChanged(); } catch (e) { fail(e, "Request refused"); } finally { setBusy(false); }
  };

  const restock = (a) => {
    const notes = window.prompt(`Return the deducted stock of cancelled order ${a.source.no || a.source.id} to the shelf? Enter a note:`);
    if (!notes) return;
    run(() => api.post(`/inventory/sales/orders/${a.source.id}/restock`, { idempotency_key: newOperationKey(), notes }),
      (res) => (res.data.outcome === "already_applied" ? "Already restocked" : `Restocked (${res.data.operation_no})`));
  };
  const resolve = (a) => {
    const notes = window.prompt(`Close the shortfall of order ${a.source.no || a.source.id} without deducting stock? Enter the reason:`);
    if (!notes) return;
    run(() => api.post(`/inventory/sales/orders/${a.source.id}/resolve-shortfall`, { notes }), "Shortfall resolved");
  };
  const retry = (a) => run(() => api.post(`/inventory/sales/orders/${a.source.id}/deduct`), (res) => `Deduction: ${res.data.outcome}`);
  const find = async () => {
    setFound(null);
    try { setFound((await api.get(`/inventory/sales/orders/${encodeURIComponent(lookup.trim())}`)).data); } catch (e) { fail(e, "No inventory record for that order"); }
  };

  return (
    <div className="space-y-4" data-testid="inventory-sales-panel">
      {!settings.sales_integration_enabled && (
        <Card className="p-4 text-sm text-muted-foreground" data-testid="inventory-sales-off">
          Sales integration is off (Setup). Orders do not hold or deduct stock until it is turned on.
        </Card>
      )}

      <Card className="p-4 space-y-2">
        <h3 className="font-semibold">Paid orders in shortfall ({ex.shortfalls.length})</h3>
        <p className="text-xs text-muted-foreground">Paid, but sellable stock could not cover them. Nothing was deducted. Retry once stock is received, or resolve with a note.</p>
        <Table>
          <TableHeader><TableRow><TableHead>Order</TableHead><TableHead>Lines</TableHead><TableHead>Since</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {ex.shortfalls.map((a) => (
              <TableRow key={a.id} data-testid={`sales-shortfall-${a.source.id}`}>
                <TableCell className="font-mono text-xs">{a.source.no || a.source.id}</TableCell>
                <TableCell><AllocationLines allocation={a} /></TableCell>
                <TableCell className="text-xs">{(a.shortfall?.at || "").slice(0, 16).replace("T", " ")} UTC</TableCell>
                <TableCell className="space-x-1 whitespace-nowrap">
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => retry(a)}>Retry</Button>
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => resolve(a)}>Resolve</Button>
                </TableCell>
              </TableRow>
            ))}
            {ex.shortfalls.length === 0 && <TableRow><TableCell colSpan={4} className="text-sm text-muted-foreground">None.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Card className="p-4 space-y-2">
        <h3 className="font-semibold">Cancelled orders awaiting restock ({ex.restock_pending.length})</h3>
        <p className="text-xs text-muted-foreground">Stock was deducted before the order was cancelled. A cancellation or refund never restocks on its own — restock only goods that are physically back on the shelf.</p>
        <Table>
          <TableHeader><TableRow><TableHead>Order</TableHead><TableHead>Lines</TableHead><TableHead>Sale</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {ex.restock_pending.map((a) => (
              <TableRow key={a.id} data-testid={`sales-restock-${a.source.id}`}>
                <TableCell className="font-mono text-xs">{a.source.no || a.source.id}</TableCell>
                <TableCell><AllocationLines allocation={a} /></TableCell>
                <TableCell className="font-mono text-xs">{a.sale_operation_no}</TableCell>
                <TableCell><Button size="sm" disabled={busy} onClick={() => restock(a)}>Restock</Button></TableCell>
              </TableRow>
            ))}
            {ex.restock_pending.length === 0 && <TableRow><TableCell colSpan={4} className="text-sm text-muted-foreground">None.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Card className="p-4 space-y-3">
        <h3 className="font-semibold">Order inventory status</h3>
        <div className="flex gap-2 items-end">
          <Field label="Order number"><Input value={lookup} onChange={(e) => setLookup(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && lookup.trim()) find(); }} placeholder="e.g. RUD-ORD-000040" className="w-96 max-w-full" data-testid="sales-lookup" /></Field>
          <Button size="sm" onClick={find} disabled={!lookup.trim()}>Look up</Button>
        </div>
        {found && (
          <div className="text-sm space-y-1" data-testid="sales-lookup-result">
            <div>
              <span className="font-mono">{found.source.no || found.source.id}</span>{" "}
              <Badge variant={STATE_VARIANT[found.state] || "outline"}>{found.state}</Badge>
              {found.sale_operation_no && <span className="ml-2 font-mono text-xs">{found.sale_operation_no}</span>}
              {found.release_reason && <span className="ml-2 text-xs text-muted-foreground">released: {found.release_reason}</span>}
            </div>
            <AllocationLines allocation={found} />
          </div>
        )}
      </Card>
    </div>
  );
}
